/**
 * GeocodingService - Address geocoding, reverse geocoding, and place search
 * using OpenStreetMap Nominatim API.
 *
 * **Validates: Requirements 27.1, 27.2, 27.3, 27.5**
 */

export interface GeocodingResult {
  /** Display name / formatted address */
  address: string;
  /** [longitude, latitude] */
  coordinates: [number, number];
  /** Confidence score 0-1 based on importance from Nominatim */
  confidence: number;
  /** Bounding box [minLon, minLat, maxLon, maxLat] */
  boundingBox?: [number, number, number, number];
  /** Place type (city, road, building, etc.) */
  placeType: string;
  /** OSM place ID */
  placeId?: string;
}

/** Nominatim API response shape */
interface NominatimResult {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  boundingbox: [string, string, string, string]; // [south, north, west, east]
  lat: string;
  lon: string;
  display_name: string;
  class: string;
  type: string;
  importance: number;
  address?: Record<string, string>;
}

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';
const USER_AGENT = 'GeoVera-WebGIS/1.0';

/**
 * Rate limiting: Nominatim requires max 1 request per second.
 */
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1100; // ms

/**
 * Reset the rate limiter (for testing purposes).
 */
export function _resetRateLimiter(): void {
  lastRequestTime = 0;
}

async function throttledFetch(url: string): Promise<Response> {
  const now = Date.now();
  const elapsed = now - lastRequestTime;
  if (elapsed < MIN_REQUEST_INTERVAL) {
    await new Promise((resolve) => setTimeout(resolve, MIN_REQUEST_INTERVAL - elapsed));
  }
  lastRequestTime = Date.now();
  return fetch(url, {
    headers: {
      'User-Agent': USER_AGENT,
      Accept: 'application/json',
    },
  });
}

/**
 * Convert Nominatim result to GeocodingResult
 */
function toGeocodingResult(item: NominatimResult): GeocodingResult {
  const [south, north, west, east] = item.boundingbox.map(Number);
  return {
    address: item.display_name,
    coordinates: [parseFloat(item.lon), parseFloat(item.lat)],
    confidence: Math.min(1, Math.max(0, item.importance)),
    boundingBox: [west, south, east, north],
    placeType: item.type || item.class || 'unknown',
    placeId: String(item.place_id),
  };
}

export class GeocodingService {
  /**
   * Geocode an address or place name to coordinates.
   *
   * **Validates: Requirement 27.1, 27.5**
   */
  async geocodeAddress(address: string): Promise<GeocodingResult[]> {
    if (!address.trim()) return [];

    const params = new URLSearchParams({
      q: address.trim(),
      format: 'json',
      addressdetails: '1',
      limit: '8',
    });

    const url = `${NOMINATIM_BASE_URL}/search?${params.toString()}`;

    try {
      const response = await throttledFetch(url);
      if (!response.ok) {
        throw new Error(`Nominatim API error: ${response.status}`);
      }
      const data: NominatimResult[] = await response.json();
      return data.map(toGeocodingResult);
    } catch (error) {
      console.error('Geocoding error:', error);
      return [];
    }
  }

  /**
   * Reverse geocode coordinates to an address.
   *
   * @param coordinates [longitude, latitude]
   */
  async reverseGeocode(coordinates: [number, number]): Promise<GeocodingResult | null> {
    const [lon, lat] = coordinates;

    const params = new URLSearchParams({
      lat: String(lat),
      lon: String(lon),
      format: 'json',
      addressdetails: '1',
    });

    const url = `${NOMINATIM_BASE_URL}/reverse?${params.toString()}`;

    try {
      const response = await throttledFetch(url);
      if (!response.ok) {
        throw new Error(`Nominatim reverse geocode error: ${response.status}`);
      }
      const data: NominatimResult = await response.json();
      if (!data || !data.lat) return null;
      return toGeocodingResult(data);
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  }

  /**
   * Search for places by query string.
   * Alias for geocodeAddress with broader search semantics.
   *
   * **Validates: Requirement 27.5**
   */
  async searchPlaces(query: string): Promise<GeocodingResult[]> {
    return this.geocodeAddress(query);
  }
}

/** Singleton instance */
export const geocodingService = new GeocodingService();
